import { create } from 'zustand';
import type { BubbleNode, ChainId, Cluster, HolderHistoryPoint, IndexStatus, MapData } from '@pleco/shared';
import { api, type MapQuery } from '../lib/api.js';

export interface ViewOptions {
  limit: number;
  magic: boolean;
  time: boolean;
  contracts: boolean;
  /** Unix seconds for time travel; undefined = live. */
  timestamp?: number;
  /** Visual: fade everything not connected to the hovered node. */
  focusMode: boolean;
  showLabels: boolean;
}

interface MapState {
  chain: ChainId;
  address: string;
  options: ViewOptions;

  data?: MapData;
  history: HolderHistoryPoint[];
  status?: IndexStatus;

  loading: boolean;
  error?: string;
  toast?: string;

  selected?: BubbleNode;
  hovered?: BubbleNode;
  selectedCluster?: Cluster;
  railOpen: boolean;

  load: (chain: ChainId, address: string, override?: Partial<ViewOptions>, refresh?: boolean) => Promise<void>;
  /** Live view: swap in a newer complete map without any loading state. */
  refreshLive: (maxAge: number) => Promise<void>;
  setOptions: (patch: Partial<ViewOptions>) => void;
  select: (node?: BubbleNode) => void;
  hover: (node?: BubbleNode) => void;
  selectCluster: (cluster?: Cluster) => void;
  setToast: (msg?: string) => void;
  toggleRail: () => void;
}

export const DEFAULT_VIEW: ViewOptions = {
  limit: 150,
  magic: true,
  // exchange-hop links: rare, and confusing to first-time users
  time: false,
  contracts: false,
  focusMode: true,
  showLabels: true,
};

const toQuery = (o: ViewOptions, refresh?: boolean): MapQuery => ({
  limit: o.limit,
  magic: o.magic,
  time: o.time,
  contracts: o.contracts,
  timestamp: o.timestamp,
  refresh,
});

let pollTimer: ReturnType<typeof setTimeout> | undefined;

export const useMapStore = create<MapState>((set, get) => ({
  chain: 'sol',
  address: '',
  options: { ...DEFAULT_VIEW },
  history: [],
  loading: false,
  railOpen: false,

  async load(chain, address, override, refresh) {
    const options = { ...get().options, ...override };
    // Another token: drop the old one at once, so its numbers are never
    // shown under the new token's address while the new map loads.
    const switching = get().address !== address || get().chain !== chain;
    set({
      chain,
      address,
      options,
      loading: true,
      error: undefined,
      ...(switching ? { data: undefined, status: undefined, history: [], selected: undefined, selectedCluster: undefined } : {}),
    });
    if (pollTimer) clearTimeout(pollTimer);

    try {
      const data = await api.map(chain, address, toQuery(options, refresh));
      // a slow answer for a token the user already left
      if (get().address !== address || get().chain !== chain) return;
      set({ data, loading: false, selected: undefined, selectedCluster: undefined });

      // keep the selected node object in sync with fresh data
      const sel = get().selected as BubbleNode | undefined;
      if (sel) set({ selected: data.nodes.find((n) => n.id === sel.id) });

      api
        .history(chain, address)
        .then(({ points }) => set({ history: points }))
        .catch(() => undefined);

      // While the backfill runs, poll until the map is complete.
      if (data.partial) {
        const poll = async () => {
          try {
            const status = await api.status(chain, address);
            set({ status });
            if (get().address !== address) return;
            if (status.state === 'ready') {
              const fresh = await api.map(chain, address, toQuery(get().options));
              if (get().address !== address) return;
              set({ data: fresh });
              api.history(chain, address).then(({ points }) => set({ history: points })).catch(() => undefined);
            } else if (status.state !== 'error' && get().address === address) {
              pollTimer = setTimeout(poll, 4000);
            }
          } catch {
            /* stop polling on error */
          }
        };
        pollTimer = setTimeout(poll, 3500);
      } else {
        set({ status: undefined });
      }
    } catch (err) {
      if (get().address !== address) return;
      set({ loading: false, error: err instanceof Error ? err.message : 'Failed to load map' });
    }
  },

  setOptions(patch) {
    const { chain, address } = get();
    const options = { ...get().options, ...patch };
    set({ options });
    // Visual-only switches don't need a round trip.
    const visualOnly = Object.keys(patch).every(
      (k) => k === 'focusMode' || k === 'showLabels' || k === 'contracts',
    );
    if (!visualOnly && address) void get().load(chain, address, patch);
  },

  async refreshLive(maxAge) {
    const { chain, address, data, options, loading } = get();
    if (!data || loading || data.partial || options.timestamp || data.token.address !== address) return;
    try {
      const fresh = await api.map(chain, address, { ...toQuery(options), maxAge });
      const cur = get().data;
      // only a newer, complete map for the token still on screen
      if (get().address !== address || !cur || fresh.partial || fresh.asOf <= cur.asOf) return;
      set({ data: fresh });
    } catch {
      /* keep the current map */
    }
  },

  select(node) {
    set({ selected: node, selectedCluster: undefined, railOpen: node ? true : get().railOpen });
  },
  hover(node) {
    set({ hovered: node });
  },
  selectCluster(cluster) {
    set({ selectedCluster: cluster, selected: undefined });
  },
  setToast(msg) {
    set({ toast: msg });
    if (msg) setTimeout(() => set((s) => (s.toast === msg ? { toast: undefined } : s)), 2600);
  },
  toggleRail() {
    set({ railOpen: !get().railOpen });
  },
}));
